import { getAuthHeaders, getBaseUrl } from './apiClient';

export async function getPresignedUrl(s3Key: string): Promise<string> {
  const resp = await fetch(
    `${getBaseUrl()}/secure/courses/presign?key=${encodeURIComponent(s3Key)}`,
    { headers: await getAuthHeaders() },
  );
  if (!resp.ok) throw new Error(`Failed to get presigned URL (${resp.status})`);
  const data = await resp.json();
  return data.url;
}

export interface CourseStageRecord {
  stage_id: string;
  status: string;
  total_cost_usd?: number;
  runs?: Array<{
    timestamp: string;
    input_tokens: number;
    output_tokens: number;
    cost_usd: number;
    call_count: number;
  }>;
}

export interface CourseRun {
  run_id: string;
  course_id: string;
  status: 'queued' | 'running' | 'paused' | 'blocked' | 'failed' | 'succeeded';
  stages: CourseStageRecord[];
  pipeline_job_id?: string | null;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  version: number;
}

export interface LessonRun {
  run_id: string;
  course_run_id: string;
  lesson_number: number;
  title: string;
  lesson_type: string;
  status: 'not_started' | 'in_progress' | 'completed' | 'published';
  stages: CourseStageRecord[];
  pipeline_job_id?: string | null;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  version: number;
}

export interface CourseDocument {
  targeting: {
    title: string;
    description: string;
    audience: string;
    cefr_level: string;
    skill_focus?: Array<{ category: string; skill: string; custom?: boolean }>;
    skills: Array<string | { id: string; name: string }>;
    skill_category: string | { id: string; name: string };
    industry_focus: string;
    suggested_lesson_count: number;
    approx_lesson_minutes: number;
    test_policy: string;
    additional_guidance: string;
  };
  syllabus: {
    narrative_arc: string;
    what_you_will_learn: string[];
    structured_goals: string[];
    lessons: Array<{
      lesson_number: number;
      title: string;
      subtitle: string;
      objectives: string[];
      prerequisites: string;
      exit_knowledge: string;
      estimated_duration_minutes: number;
      component_hints: string[];
      targeting_context: string;
    }>;
  } | null;
  metadata: {
    author: string;
    tier: string;
    tags: Array<{ name: string; category: string }>;
    key: string;
  };
  publication: {
    status: string;
    production_course_id?: string;
    published_at?: string;
  };
}

export const courseRunsService = {
  async list(courseId?: string): Promise<{ runs: CourseRun[]; count: number }> {
    const qs = courseId ? `?course_id=${encodeURIComponent(courseId)}` : '';
    const resp = await fetch(`${getBaseUrl()}/secure/courses/runs${qs}`, {
      headers: await getAuthHeaders(),
    });
    if (!resp.ok) throw new Error(`Failed to list runs (${resp.status})`);
    return resp.json();
  },

  async get(runId: string): Promise<CourseRun> {
    const resp = await fetch(
      `${getBaseUrl()}/secure/courses/runs/${encodeURIComponent(runId)}`,
      { headers: await getAuthHeaders() },
    );
    if (!resp.ok) throw new Error(`Failed to get run (${resp.status})`);
    return resp.json();
  },

  async create(courseId: string): Promise<CourseRun> {
    const resp = await fetch(`${getBaseUrl()}/secure/courses/runs`, {
      method: 'POST',
      headers: await getAuthHeaders(),
      body: JSON.stringify({ course_id: courseId }),
    });
    if (!resp.ok) {
      const text = await resp.text();
      throw new Error(`Failed to create run: ${resp.status} ${text}`);
    }
    return resp.json();
  },

  async update(runId: string, payload: Partial<CourseRun>): Promise<CourseRun> {
    const resp = await fetch(
      `${getBaseUrl()}/secure/courses/runs/${encodeURIComponent(runId)}`,
      {
        method: 'PUT',
        headers: await getAuthHeaders(),
        body: JSON.stringify(payload),
      },
    );
    if (!resp.ok) {
      const text = await resp.text();
      throw new Error(`Failed to update run: ${resp.status} ${text}`);
    }
    return resp.json();
  },

  async getDocument(runId: string): Promise<CourseDocument> {
    const resp = await fetch(
      `${getBaseUrl()}/secure/courses/runs/${encodeURIComponent(runId)}/document`,
      { headers: await getAuthHeaders() },
    );
    if (!resp.ok) throw new Error(`Failed to get document (${resp.status})`);
    return resp.json();
  },

  async updateDocument(runId: string, patch: Partial<CourseDocument>): Promise<CourseDocument> {
    const resp = await fetch(
      `${getBaseUrl()}/secure/courses/runs/${encodeURIComponent(runId)}/document`,
      {
        method: 'PUT',
        headers: await getAuthHeaders(),
        body: JSON.stringify(patch),
      },
    );
    if (!resp.ok) throw new Error(`Failed to update document (${resp.status})`);
    return resp.json();
  },

  async executeStage(runId: string, stageId: string): Promise<{ job_id: string }> {
    const resp = await fetch(
      `${getBaseUrl()}/secure/courses/runs/${encodeURIComponent(runId)}/execute`,
      {
        method: 'POST',
        headers: await getAuthHeaders(),
        body: JSON.stringify({ stage_id: stageId }),
      },
    );
    if (!resp.ok) {
      const text = await resp.text();
      throw new Error(`Failed to execute stage: ${resp.status} ${text}`);
    }
    return resp.json();
  },

  async renderPrompt(runId: string, stageId: string): Promise<{
    template: string;
    rendered_prompt: string;
    variables: Record<string, unknown>;
    model: string;
    temperature: number | null;
    max_tokens: number | null;
    thinking: { type: string; budget_tokens: number } | null;
    system_prompt: string;
    source: 'prompt_instances' | 'pipeline_config';
  }> {
    const resp = await fetch(
      `${getBaseUrl()}/secure/courses/runs/${encodeURIComponent(runId)}/render-prompt/${encodeURIComponent(stageId)}`,
      {
        method: 'POST',
        headers: await getAuthHeaders(),
        body: '{}',
      },
    );
    if (!resp.ok) throw new Error(`Failed to render prompt (${resp.status})`);
    return resp.json();
  },

  async getStageRaw(runId: string, stageId: string, extractionId?: string): Promise<string> {
    const qs = extractionId ? `?extraction_id=${encodeURIComponent(extractionId)}` : '';
    const resp = await fetch(
      `${getBaseUrl()}/secure/courses/runs/${encodeURIComponent(runId)}/stage-outputs/${encodeURIComponent(stageId)}/raw${qs}`,
      { headers: await getAuthHeaders() },
    );
    const text = await resp.text();
    if (!resp.ok) throw new Error(text || `Failed to get stage raw (${resp.status})`);
    return text;
  },

  async getStagePrompt(runId: string, stageId: string, extractionId?: string): Promise<string> {
    const qs = extractionId
      ? `?extraction_id=${encodeURIComponent(extractionId)}&type=prompt`
      : '?type=prompt';
    const resp = await fetch(
      `${getBaseUrl()}/secure/courses/runs/${encodeURIComponent(runId)}/stage-outputs/${encodeURIComponent(stageId)}/raw${qs}`,
      { headers: await getAuthHeaders() },
    );
    const text = await resp.text();
    if (!resp.ok) throw new Error(text || `Failed to get stage prompt (${resp.status})`);
    return text;
  },

  async savePromptOverride(
    runId: string,
    stageId: string,
    override: Record<string, unknown>,
  ): Promise<CourseRun> {
    const run = await courseRunsService.get(runId);
    const runAny = run as unknown as Record<string, unknown>;
    const existing = (runAny.prompt_instances as Record<string, unknown>) || {};
    const updated = {
      ...existing,
      [stageId]: {
        ...((existing[stageId] as Record<string, unknown>) || {}),
        ...override,
      },
    };
    return courseRunsService.update(runId, { prompt_instances: updated } as unknown as Partial<CourseRun>);
  },

  async lockSyllabus(runId: string): Promise<{ locked: boolean; lesson_runs_created: number; lesson_runs: Array<{ run_id: string; lesson_number: number; title: string }> }> {
    const resp = await fetch(
      `${getBaseUrl()}/secure/courses/runs/${encodeURIComponent(runId)}/lock-syllabus`,
      {
        method: 'POST',
        headers: await getAuthHeaders(),
        body: '{}',
      },
    );
    if (!resp.ok) {
      const text = await resp.text();
      throw new Error(`Failed to lock syllabus: ${resp.status} ${text}`);
    }
    return resp.json();
  },

  async buildCourse(courseRunId: string): Promise<{
    course_run_id: string;
    lessons: number;
    jobs: Array<{ lesson_run_id: string; lesson_number: number; title: string; job_id: string }>;
  }> {
    const resp = await fetch(
      `${getBaseUrl()}/secure/courses/runs/${encodeURIComponent(courseRunId)}/build`,
      { method: 'POST', headers: await getAuthHeaders(), body: '{}' },
    );
    if (!resp.ok) {
      const text = await resp.text();
      throw new Error(`Failed to build course: ${resp.status} ${text}`);
    }
    return resp.json();
  },

  async buildLesson(lessonRunId: string): Promise<{ job_id: string; run_id: string }> {
    const resp = await fetch(
      `${getBaseUrl()}/secure/courses/lesson-runs/${encodeURIComponent(lessonRunId)}/build`,
      { method: 'POST', headers: await getAuthHeaders(), body: '{}' },
    );
    if (!resp.ok) {
      const text = await resp.text();
      throw new Error(`Failed to build lesson: ${resp.status} ${text}`);
    }
    return resp.json();
  },

  async listLessonRuns(courseRunId: string): Promise<{ lesson_runs: LessonRun[]; count: number }> {
    const resp = await fetch(
      `${getBaseUrl()}/secure/courses/lesson-runs?course_run_id=${encodeURIComponent(courseRunId)}`,
      { headers: await getAuthHeaders() },
    );
    if (!resp.ok) throw new Error(`Failed to list lesson runs (${resp.status})`);
    return resp.json();
  },

  async getLessonRun(runId: string): Promise<LessonRun> {
    const resp = await fetch(
      `${getBaseUrl()}/secure/courses/lesson-runs/${encodeURIComponent(runId)}`,
      { headers: await getAuthHeaders() },
    );
    if (!resp.ok) throw new Error(`Failed to get lesson run (${resp.status})`);
    return resp.json();
  },

  async getLessonDocument(lessonRunId: string): Promise<Record<string, unknown>> {
    const resp = await fetch(
      `${getBaseUrl()}/secure/courses/lesson-runs/${encodeURIComponent(lessonRunId)}/document`,
      { headers: await getAuthHeaders() },
    );
    if (!resp.ok) throw new Error(`Failed to get lesson document (${resp.status})`);
    return resp.json();
  },

  async approveLessonStage(lessonRunId: string, stageId: string): Promise<{ approved: boolean }> {
    const resp = await fetch(
      `${getBaseUrl()}/secure/courses/lesson-runs/${encodeURIComponent(lessonRunId)}/approve-stage`,
      {
        method: 'POST',
        headers: await getAuthHeaders(),
        body: JSON.stringify({ stage_id: stageId }),
      },
    );
    if (!resp.ok) {
      const text = await resp.text();
      throw new Error(`Failed to approve stage: ${resp.status} ${text}`);
    }
    return resp.json();
  },

  async executeLessonStage(
    lessonRunId: string,
    stageId: string,
    extractionId?: string,
    options?: { ttsProvider?: string; ttsModel?: string; voice?: string },
  ): Promise<{ job_id: string }> {
    const payload: Record<string, string> = { stage_id: stageId };
    if (extractionId) payload.extraction_id = extractionId;
    if (options?.ttsProvider) payload.tts_provider = options.ttsProvider;
    if (options?.ttsModel) payload.tts_model = options.ttsModel;
    if (options?.voice) payload.voice = options.voice;
    const resp = await fetch(
      `${getBaseUrl()}/secure/courses/lesson-runs/${encodeURIComponent(lessonRunId)}/execute`,
      {
        method: 'POST',
        headers: await getAuthHeaders(),
        body: JSON.stringify(payload),
      },
    );
    if (!resp.ok) {
      const text = await resp.text();
      throw new Error(`Failed to execute lesson stage: ${resp.status} ${text}`);
    }
    return resp.json();
  },

  async renderLessonPrompt(lessonRunId: string, stageId: string, extractionId?: string): Promise<{
    template: string;
    rendered_prompt: string;
    variables: Record<string, unknown>;
    model: string;
    temperature: number | null;
    max_tokens: number | null;
    thinking: { type: string; budget_tokens: number } | null;
    system_prompt: string;
    source: 'prompt_instances' | 'pipeline_config';
  }> {
    const resp = await fetch(
      `${getBaseUrl()}/secure/courses/lesson-runs/${encodeURIComponent(lessonRunId)}/render-prompt/${encodeURIComponent(stageId)}`,
      {
        method: 'POST',
        headers: await getAuthHeaders(),
        body: JSON.stringify(extractionId ? { extraction_id: extractionId } : {}),
      },
    );
    if (!resp.ok) throw new Error(`Failed to render lesson prompt (${resp.status})`);
    return resp.json();
  },

  async getLessonStageRaw(lessonRunId: string, stageId: string, extractionId?: string): Promise<string> {
    const qs = extractionId ? `?extraction_id=${encodeURIComponent(extractionId)}` : '';
    const resp = await fetch(
      `${getBaseUrl()}/secure/courses/lesson-runs/${encodeURIComponent(lessonRunId)}/stage-outputs/${encodeURIComponent(stageId)}/raw${qs}`,
      { headers: await getAuthHeaders() },
    );
    const text = await resp.text();
    if (!resp.ok) throw new Error(text || `Failed to get lesson stage raw (${resp.status})`);
    return text;
  },

  async getLessonStagePrompt(lessonRunId: string, stageId: string, extractionId?: string): Promise<string> {
    const qs = extractionId
      ? `?extraction_id=${encodeURIComponent(extractionId)}&type=prompt`
      : '?type=prompt';
    const resp = await fetch(
      `${getBaseUrl()}/secure/courses/lesson-runs/${encodeURIComponent(lessonRunId)}/stage-outputs/${encodeURIComponent(stageId)}/raw${qs}`,
      { headers: await getAuthHeaders() },
    );
    const text = await resp.text();
    if (!resp.ok) throw new Error(text || `Failed to get lesson stage prompt (${resp.status})`);
    return text;
  },

  async updateLessonDocument(lessonRunId: string, patch: Record<string, unknown>): Promise<Record<string, unknown>> {
    const resp = await fetch(
      `${getBaseUrl()}/secure/courses/lesson-runs/${encodeURIComponent(lessonRunId)}/document`,
      {
        method: 'PUT',
        headers: await getAuthHeaders(),
        body: JSON.stringify(patch),
      },
    );
    if (!resp.ok) throw new Error(`Failed to update lesson document (${resp.status})`);
    return resp.json();
  },

  async updateLessonRun(lessonRunId: string, payload: Record<string, unknown>): Promise<LessonRun> {
    const resp = await fetch(
      `${getBaseUrl()}/secure/courses/lesson-runs/${encodeURIComponent(lessonRunId)}`,
      { method: 'PUT', headers: await getAuthHeaders(), body: JSON.stringify(payload) },
    );
    if (!resp.ok) {
      const text = await resp.text();
      throw new Error(`Failed to update lesson run: ${resp.status} ${text}`);
    }
    return resp.json();
  },

  async saveLessonPromptOverride(
    lessonRunId: string,
    stageId: string,
    override: Record<string, unknown>,
  ): Promise<LessonRun> {
    const run = await courseRunsService.getLessonRun(lessonRunId);
    const runAny = run as unknown as Record<string, unknown>;
    const existing = (runAny.prompt_instances as Record<string, unknown>) || {};
    const updated = {
      ...existing,
      [stageId]: { ...((existing[stageId] as Record<string, unknown>) || {}), ...override },
    };
    return courseRunsService.updateLessonRun(lessonRunId, { prompt_instances: updated });
  },

  async clearLessonPromptOverride(lessonRunId: string, stageId: string): Promise<LessonRun> {
    const run = await courseRunsService.getLessonRun(lessonRunId);
    const runAny = run as unknown as Record<string, unknown>;
    const existing = { ...((runAny.prompt_instances as Record<string, unknown>) || {}) };
    delete existing[stageId];
    return courseRunsService.updateLessonRun(lessonRunId, { prompt_instances: existing });
  },

  async clearPromptOverride(runId: string, stageId: string): Promise<CourseRun> {
    const run = await courseRunsService.get(runId);
    const runAny = run as unknown as Record<string, unknown>;
    const existing = { ...((runAny.prompt_instances as Record<string, unknown>) || {}) };
    delete existing[stageId];
    return courseRunsService.update(runId, { prompt_instances: existing } as unknown as Partial<CourseRun>);
  },

  async getArtworkStatus(courseRunId: string): Promise<{ artwork: any }> {
    const resp = await fetch(
      `${getBaseUrl()}/secure/courses/runs/${encodeURIComponent(courseRunId)}/artwork`,
      { headers: await getAuthHeaders() },
    );
    if (!resp.ok) throw new Error(`Failed to get artwork status (${resp.status})`);
    return resp.json();
  },

  async generateArtwork(courseRunId: string, prompt?: string, imageModel?: string): Promise<{ job_id: string }> {
    const resp = await fetch(
      `${getBaseUrl()}/secure/courses/runs/${encodeURIComponent(courseRunId)}/artwork`,
      {
        method: 'POST',
        headers: await getAuthHeaders(),
        body: JSON.stringify({
          ...(prompt ? { prompt } : {}),
          ...(imageModel ? { image_model: imageModel } : {}),
        }),
      },
    );
    if (!resp.ok) {
      const err = await resp.json().catch(() => ({ error: `Artwork generation failed (${resp.status})` }));
      throw new Error(err.error || `Artwork generation failed (${resp.status})`);
    }
    return resp.json();
  },

  async getExportStatus(courseRunId: string): Promise<{
    lessons: Array<{ run_id: string; lesson_number: number; title: string; status: string; compiled: boolean }>;
    total: number;
    compiled_count: number;
  }> {
    const resp = await fetch(
      `${getBaseUrl()}/secure/courses/runs/${encodeURIComponent(courseRunId)}/export-status`,
      { headers: await getAuthHeaders() },
    );
    if (!resp.ok) throw new Error(`Failed to get export status (${resp.status})`);
    return resp.json();
  },

  async exportCourse(courseRunId: string): Promise<{
    download_url: string;
    zip_key: string;
    zip_size_bytes: number;
    included_lessons: Array<{ lesson_number: number; title: string }>;
    skipped_lessons: Array<{ lesson_number: number; title: string; reason: string }>;
  }> {
    const resp = await fetch(
      `${getBaseUrl()}/secure/courses/runs/${encodeURIComponent(courseRunId)}/export`,
      { method: 'POST', headers: await getAuthHeaders() },
    );
    if (!resp.ok) {
      const err = await resp.json().catch(() => ({ error: `Export failed (${resp.status})` }));
      throw new Error(err.error || `Export failed (${resp.status})`);
    }
    return resp.json();
  },
};
